import { apiClient, handleApiError } from './api'
import { BaseService } from './base/BaseService'

export type IntegrationConfigValue =
  | string
  | number
  | boolean
  | Record<string, boolean>
  | Record<string, string>

export type IntegrationConnectionStatus =
  | 'never_validated'
  | 'connected'
  | 'connection_broken'

export interface IntegrationData {
  integrationType: string
  connected: boolean
  enabled: boolean
  config: Record<string, IntegrationConfigValue>
  connectionStatus?: IntegrationConnectionStatus
  createdAt: string | null
  updatedAt: string | null
}

export interface IntegrationUpdatePayload {
  config: Record<string, IntegrationConfigValue>
  secrets?: Record<string, string>
  enabled?: boolean
}

export interface TestConnectionResult {
  ok: boolean
  error?: string
}

class IntegrationsService extends BaseService {
  protected readonly baseURL = '/organizations'

  async getIntegrations(): Promise<IntegrationData[]> {
    return this.get<IntegrationData[]>(
      `/${this.getOrganizationId()}/integrations`
    )
  }

  async getIntegration(type: string): Promise<IntegrationData> {
    return this.get<IntegrationData>(
      `/${this.getOrganizationId()}/integrations/${type}`
    )
  }

  async updateIntegration(
    type: string,
    payload: IntegrationUpdatePayload
  ): Promise<IntegrationData> {
    return this.put<IntegrationData>(
      `/${this.getOrganizationId()}/integrations/${type}`,
      payload
    )
  }

  async testConnection(
    type: string,
    privateApiKey: string
  ): Promise<TestConnectionResult> {
    // This endpoint returns a raw `{ ok, error }` body (no `{ success, data }`
    // envelope), so it bypasses handleApiResponse and reads the body directly.
    try {
      const response = await apiClient.post<TestConnectionResult>(
        `${this.baseURL}/${this.getOrganizationId()}/integrations/${type}/test_connection`,
        { privateApiKey }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  // ClickPoint's test_connection reads its key from top-level `api_key`
  // (not `private_api_key` like Klaviyo), and re-validates against the
  // integration's already-saved config server-side — so this only ever
  // exercises a config that has been saved already.
  async testClickpointConnection(
    apiKey: string
  ): Promise<TestConnectionResult> {
    try {
      const response = await apiClient.post<TestConnectionResult>(
        `${this.baseURL}/${this.getOrganizationId()}/integrations/clickpoint/test_connection`,
        { apiKey }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async deleteIntegration(type: string): Promise<void> {
    await this.delete(`/${this.getOrganizationId()}/integrations/${type}`)
  }

  async requestIntegration(
    integrationName: string,
    description: string
  ): Promise<void> {
    await this.post(
      `/${this.getOrganizationId()}/integrations/request_integration`,
      { integrationName, description }
    )
  }

  async requestProfilePropertiesContact(): Promise<void> {
    await this.post(
      `/${this.getOrganizationId()}/profile_properties/contact`,
      {}
    )
  }
}

export const integrationsService = new IntegrationsService()
